import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { QrScanner } from '@components/Utilities/QrScanner';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Customer } from '@api/Customers/Customer';
import * as Yup from 'yup';
import { normalizePhone } from '@api/Customers/Customer';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { showAlertError } from '@utils/Alerts/Error';
import { hideLoader, showLoader } from '@utils/Loader';
import CustomerService from '@api/Customers/CustomerCtrl';
import { NewCustomerData } from '@api/Customers/CustomerServ';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';

interface ModalGetCustomerProps {
    modalGetCustomer: OpenModalHookType;
    onSelectCustomer: (customer: Customer) => void;
    // D6 (plan espejo): cuando el modal se abre solo al iniciar un pedido, el
    // cajero necesita una salida obvia — identificar al cliente es OPCIONAL.
    showSkip?: boolean;
    // Ajuste QA 2026-08-26: mandar la identificación A LA PANTALLA del cliente
    // (el espejo abre su teclado de teléfono). Solo aparece si hay callback.
    onRequestOnScreen?: () => void;
    // Alta desde el espejo: el cliente ya capturó su teléfono en la pantalla —
    // el modal abre directo en "Nuevo cliente" con el número precargado.
    prefillPhone?: string;
}

// El correo se quitó del flujo (ajuste QA 2026-08-26): nunca se usaba en el
// alta desde el POS — NewCustomerData lo sigue aceptando y se manda vacío.
const addCustomerSchema = Yup.object({
    firstName: Yup.string().required('Nombres requerido'),
    lastName: Yup.string(),
    phone: Yup.string()
        .required('Telefono requerido')
        .test('phone-10', 'El teléfono debe tener 10 dígitos', (value) => normalizePhone(value).length === 10),
    email: Yup.string()
});

export const ModalGetCustomer: React.FunctionComponent<ModalGetCustomerProps> = (props) => {
    const { modalGetCustomer, onSelectCustomer, showSkip, onRequestOnScreen, prefillPhone } = props;
    const customerService = useMemo(() => new CustomerService(), []);
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const [newCustomer, setNewCustomer] = useState<boolean>(false);
    const [phone, setPhone] = useState<string>('');
    const [scannerOpen, setScannerOpen] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<NewCustomerData>({
        resolver: yupResolver(addCustomerSchema),
        defaultValues: { firstName: '', lastName: '', phone: '', email: '' }
    });

    // Alta desde el espejo: abrir directo en "Nuevo cliente" con el teléfono
    // que el cliente capturó en su pantalla
    useEffect(() => {
        if (!modalGetCustomer.modalOpen || !prefillPhone) return;
        setNewCustomer(true);
        reset({ firstName: '', lastName: '', phone: prefillPhone, email: '' });
    }, [modalGetCustomer.modalOpen, prefillPhone]);

    const handleClose = () => {
        modalGetCustomer.toggle();
        clearStates();
    };

    const clearStates = () => {
        reset();
        setPhone('');
    };

    const onAddCustomerForm: SubmitHandler<NewCustomerData> = async (values) => {
        showLoader();
        try {
            const customer = await customerService.saveCustomer({
                ...values,
                branchOfficeName: branchOfficeSelected?.name
            });
            handleClose();
            onSelectCustomer(customer);
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * Busca al cliente por teléfono.
     *
     * Recibe el número como argumento (y no solo del estado) porque el escáner
     * lo entrega y busca en el mismo paso: `setPhone` no está aplicado todavía
     * cuando el código se lee.
     */
    const onGetCustomer = async (phoneToSearch?: string) => {
        const phoneValue = phoneToSearch ?? phone;
        showLoader();
        try {
            // Contar DÍGITOS, no caracteres: "221 364 5546" tiene 12 caracteres y
            // pasaba este check, pero la búsqueda es por los 10 dígitos
            if (normalizePhone(phoneValue).length !== 10) {
                showAlertError('Número invalido');
            } else {
                const customer = await customerService.getCustomerByPhone(phoneValue);
                if (customer) {
                    // Corregir los puntos del cliente al agregarlo al pedido.
                    let selectedCustomer = customer;
                    if (customer.uid) {
                        await customerService.correctUserPointsBasedOnOrders(customer.uid);
                        selectedCustomer = await customerService.getCustomer(customer.uid);
                    }
                    handleClose();
                    onSelectCustomer(selectedCustomer);
                } else {
                    showAlertError('Cliente no encontrado');
                }
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * El QR de la tarjeta de lealtad (Google Wallet) lleva SOLO el teléfono a 10
     * dígitos en texto plano — la misma llave con la que se busca al cliente. Se
     * pasa por `normalizePhone` igual que lo tecleado: un código de otra
     * procedencia no puede meter basura a la búsqueda.
     */
    const onScanQr = (value: string) => {
        const scannedPhone = normalizePhone(value);
        if (scannedPhone.length !== 10) {
            showAlertError('Ese código no es una tarjeta de lealtad de Krispis');
            return;
        }
        setPhone(scannedPhone);
        void onGetCustomer(scannedPhone);
    };

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalGetCustomer.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-add-customer-title"
            aria-describedby="modal-add-customer-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-add-customer-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                        <Stack direction="row" alignItems="center">
                            {newCustomer && (
                                <IconButton onClick={() => setNewCustomer(false)}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            )}
                            <Typography variant="h6" fontWeight={600}>
                                {newCustomer ? 'Nuevo cliente' : 'Agregar un cliente al pedido'}
                            </Typography>
                        </Stack>
                        {!newCustomer && (
                            <Typography variant="body2">
                                Busca un cliente existente con su numero de telefono o agrega un cliente nuevo
                            </Typography>
                        )}
                        {newCustomer && (
                            <Typography variant="body2">
                                Completa los datos, guarda y agrega al nuevo cliente
                            </Typography>
                        )}
                    </Stack>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'visible' }}>
                {!newCustomer && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="stretch">
                                <TextField
                                    fullWidth
                                    label="Cliente"
                                    placeholder="Número de telefono"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    // Un lector de código de barras físico se
                                    // comporta como un teclado y termina con Enter:
                                    // así el escaneo busca solo, sin tocar nada.
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            void onGetCustomer();
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setScannerOpen(true)}
                                    sx={{ minWidth: 56, px: 1 }}
                                    aria-label="Escanear tarjeta de lealtad"
                                >
                                    <QrCodeScannerIcon />
                                </Button>
                            </Stack>
                        </Grid>
                        {onRequestOnScreen && (
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="info"
                                    onClick={() => {
                                        onRequestOnScreen();
                                        handleClose();
                                    }}
                                >
                                    Que el cliente lo capture en su pantalla
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}
                {newCustomer && (
                    <form id="add-customer-form" onSubmit={handleSubmit(onAddCustomerForm)}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Controller
                                    name="firstName"
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Nombres del cliente"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.firstName}
                                            helperText={errors.firstName?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="lastName"
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            label="Apellidos del cliente"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.lastName}
                                            helperText={errors.lastName?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="phone"
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Telefono del cliente"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.phone}
                                            helperText={errors.phone?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </form>
                )}
            </DialogContent>
            <DialogActions>
                {newCustomer && (
                    <Button fullWidth color="primary" variant="contained" type="submit" form="add-customer-form">
                        Guardar y agregar al pedido
                    </Button>
                )}
                {!newCustomer && (
                    <Grid container spacing={2}>
                        {showSkip && (
                            <Grid item xs={12} md={4}>
                                <Button onClick={handleClose} fullWidth color="secondary" variant="text">
                                    Omitir
                                </Button>
                            </Grid>
                        )}
                        <Grid item xs={12} md={showSkip ? 4 : 6}>
                            <Button onClick={() => setNewCustomer(true)} fullWidth color="primary" variant="outlined">
                                Nuevo cliente
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={showSkip ? 4 : 6}>
                            <Button onClick={() => onGetCustomer()} fullWidth color="primary" variant="contained">
                                Agregar cliente
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </DialogActions>
            <QrScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={onScanQr}
                title="Escanear tarjeta de lealtad"
                helperText="Pide al cliente que abra su tarjeta Krispis en Google Wallet"
            />
        </Dialog>
    );
};
